import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const AuthContainer = styled.div`
  max-width: 400px;
  margin: 50px auto;
  padding: 30px;
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 194, 255, 0.2);
`;

const AuthTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  color: #ffffff;
  font-size: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #c4cdda;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border-radius: 8px;
  border: 1px solid rgba(0, 194, 255, 0.3);
  background: rgba(18, 34, 64, 0.4);
  color: #ffffff;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #00c2ff;
    box-shadow: 0 0 0 2px rgba(0, 194, 255, 0.2);
  }
`;

const AuthButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  color: #0c162d;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
  }
`;

const AuthLink = styled.p`
  text-align: center;
  margin-top: 20px;
  color: #c4cdda;
  
  a {
    color: #64ffda;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  text-align: center;
  margin-bottom: 15px;
  padding: 10px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
`;

const SuccessMessage = styled.div`
  color: #64ffda;
  text-align: center;
  margin-bottom: 15px;
  padding: 10px;
  background: rgba(100, 255, 218, 0.1);
  border-radius: 8px;
`;

const VerifyButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(90deg, #64ffda, #00c2ff);
  color: #0c162d;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(100, 255, 218, 0.4);
  }
`;

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userCode, setUserCode] = useState('');
  const navigate = useNavigate();

  const handleEmailVerification = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple email validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    // In a real app, you would send a verification code to the email
    // For demo purposes, we'll generate a random 6-digit code
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setVerificationCode(code);
    setIsEmailVerified(true);
    setSuccess(`Verification code sent to ${email}`);
    
    // In a real app, you would send this code to the user's email
    console.log(`Verification code: ${code}`);
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (userCode === verificationCode) {
      setSuccess('Email verified successfully!');
      setError('');
    } else {
      setError('Invalid verification code. Please try again.');
      setSuccess('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (!isEmailVerified) {
      setError('Please verify your email address first');
      return;
    }
    
    if (userCode !== verificationCode) {
      setError('Please verify your email address first');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // In a real app, you would make an API call here
    // For demo purposes, we'll just simulate a successful registration
    console.log('Registration attempt with:', { name, email, promoCode, password });
    
    // Set authentication state
    localStorage.setItem('isAuthenticated', 'true');
    
    // Redirect to dashboard after successful registration
    navigate('/dashboard');
  };

  return (
    <AuthContainer>
      <AuthTitle>Register with UPM Traders</AuthTitle>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Full Name</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            disabled={isEmailVerified && userCode === verificationCode}
          />
          {!isEmailVerified ? (
            <VerifyButton onClick={handleEmailVerification}>Send Verification Code</VerifyButton>
          ) : userCode !== verificationCode ? (
            <>
              <Input
                type="text"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value)}
                placeholder="Enter verification code"
                style={{ marginTop: '10px' }}
              />
              <VerifyButton onClick={handleVerifyCode}>Verify Code</VerifyButton>
            </>
          ) : (
            <SuccessMessage>Email verified successfully!</SuccessMessage>
          )}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="promoCode">Promo Code (Optional)</Label>
          <Input
            type="text"
            id="promoCode"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            placeholder="Enter promo code if you have one"
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a password"
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
          />
        </FormGroup>
        
        <AuthButton type="submit">Register</AuthButton>
      </form>
      
      <AuthLink>
        Already have an account? <a href="/login">Login here</a>
      </AuthLink>
    </AuthContainer>
  );
};

export default Register;